import type { ArtistProfile } from '../types';

/**
 * Визитка художника картинкой: её бросают в чат, пост или сторис вместо ссылки.
 * Поэтому на ней не «кто это», а «почему стоит заказать»: чем занят, когда
 * свободен, сколько стоит и что уже сделано.
 *
 * Рисуем на canvas вручную, а не снимаем DOM: снимок утащил бы в кадр куски
 * интерфейса, а библиотека для этого весит больше, чем весь этот файл.
 */
export interface ProfileCardService {
  title: string;
  /** Готовая строка цены, например «3 500 ₽». `null` — по договорённости. */
  price: string | null;
  /** Срок словами, например «4 дня». */
  duration: string | null;
}

export interface ProfileCardData {
  profile: ArtistProfile;
  /** Ближайший свободный день. `null` — прогноза нет. */
  nextOpening: string | null;
  /** Почта показывается, только если художник открыл её публично. */
  contactEmail: string | null;
  publicUrl: string;
  services: ProfileCardService[];
  /** Адреса работ для сетки. Берутся первые три. */
  works: string[];
}

/**
 * Готовая карточка в двух видах. `dataUrl` — для показа и сохранения: во
 * встроенном браузере Telegram `blob:` не открывается и не скачивается, меню
 * «Download» по нему молча ничего не делает. `blob` нужен только системному
 * «поделиться», которому нужен `File`.
 */
export interface ProfileCard {
  blob: Blob;
  dataUrl: string;
}

const WIDTH = 1080;
const HEIGHT = 1350;
const PADDING = 64;
const CONTENT = WIDTH - PADDING * 2;

const COLORS = {
  background: '#16151E',
  surface: '#1F1E2A',
  line: '#2C2A3A',
  ink: '#F1EFF7',
  body: '#C9C4DA',
  muted: '#9995AC',
  accent: '#8F7DE3',
  accentInk: '#B9A8FF',
  accentSoft: '#2A2440',
  okInk: '#6ECC9F',
  okSoft: '#17302A',
  okDot: '#2F9E6B',
  warnInk: '#FF9C90',
  warnSoft: '#2A1D22',
  warnDot: '#E2564D',
};

const SANS = '"Plus Jakarta Sans", system-ui, sans-serif';
const MONO = '"JetBrains Mono", ui-monospace, monospace';

export async function renderProfileCard(data: ProfileCardData): Promise<ProfileCard> {
  const canvas = document.createElement('canvas');
  canvas.width = WIDTH;
  canvas.height = HEIGHT;

  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('Браузер не дал нарисовать карточку');
  }

  /*
   * Шрифты подключены через CSS и грузятся асинхронно, а canvas ждать не умеет:
   * начни рисовать раньше — и надписи лягут системным шрифтом другой ширины.
   */
  await document.fonts?.ready;

  context.fillStyle = COLORS.background;
  context.fillRect(0, 0, WIDTH, HEIGHT);
  drawGlow(context);

  const [avatar, works] = await Promise.all([
    loadImage(data.profile.avatarUrl),
    Promise.all(data.works.slice(0, 3).map(loadImage)),
  ]);

  let y = drawHeader(context, data, avatar, PADDING);
  y = drawChips(context, data, y + 30);
  y = drawDescription(context, data.profile.description, y + 30);
  y = drawServices(context, data.services, y + 30);

  // Подвал и теги прибиты к низу, работы забирают всё, что осталось между.
  const footerTop = drawFooter(context, data);
  const tagsTop = drawTags(context, data.profile.tags, footerTop - 28 - 54);
  drawWorks(context, works, y + 30, tagsTop - 24);

  return { blob: await toBlob(canvas), dataUrl: canvas.toDataURL('image/png') };
}

/** Пятно акцента в углу: без него карточка выглядит скриншотом таблицы. */
function drawGlow(context: CanvasRenderingContext2D): void {
  const gradient = context.createRadialGradient(WIDTH - 60, 40, 0, WIDTH - 60, 40, 460);
  gradient.addColorStop(0, 'rgba(143, 125, 227, 0.34)');
  gradient.addColorStop(1, 'rgba(143, 125, 227, 0)');

  context.fillStyle = gradient;
  context.fillRect(0, 0, WIDTH, 520);
}

function drawHeader(
  context: CanvasRenderingContext2D,
  data: ProfileCardData,
  avatar: HTMLImageElement | null,
  top: number,
): number {
  const size = 172;

  context.save();
  roundedRect(context, PADDING, top, size, size, 36);
  context.clip();

  if (avatar) {
    drawCover(context, avatar, PADDING, top, size, size);
  } else {
    context.fillStyle = COLORS.accent;
    context.fillRect(PADDING, top, size, size);
    context.fillStyle = '#fff';
    context.font = `800 76px ${SANS}`;
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillText(
      data.profile.displayName.slice(0, 1).toUpperCase(),
      PADDING + size / 2,
      top + size / 2,
    );
    context.textAlign = 'left';
    context.textBaseline = 'alphabetic';
  }
  context.restore();

  const left = PADDING + size + 32;
  const width = WIDTH - left - PADDING;

  context.fillStyle = COLORS.ink;
  context.font = `800 76px ${SANS}`;
  fitText(context, data.profile.displayName, left, top + 88, width);

  context.fillStyle = COLORS.muted;
  context.font = `500 30px ${MONO}`;
  fitText(context, `@${data.profile.publicLink.slug}`, left, top + 136, width);

  return top + size;
}

/** Статус, ближайшее окно и почта — по ним и решают, писать ли вообще. */
function drawChips(context: CanvasRenderingContext2D, data: ProfileCardData, top: number): number {
  const open = data.profile.commissionsStatus !== 'CLOSED';
  const chips: { text: string; dot?: string; ink: string; back: string; font: string }[] = [
    {
      text:
        data.profile.commissionsStatus === 'OPEN'
          ? 'Принимаю заказы'
          : data.profile.commissionsStatus === 'WAITLIST'
            ? 'Лист ожидания'
            : 'Заказы закрыты',
      dot: open ? COLORS.okDot : COLORS.warnDot,
      ink: open ? COLORS.okInk : COLORS.warnInk,
      back: open ? COLORS.okSoft : COLORS.warnSoft,
      font: `700 26px ${SANS}`,
    },
  ];

  if (data.nextOpening) {
    chips.push({
      text: `Свободен с ${humanDate(data.nextOpening)}`,
      ink: COLORS.body,
      back: COLORS.surface,
      font: `600 26px ${SANS}`,
    });
  }

  if (data.contactEmail) {
    chips.push({
      text: data.contactEmail,
      ink: COLORS.accentInk,
      back: COLORS.accentSoft,
      font: `600 24px ${MONO}`,
    });
  }

  const height = 58;
  let x = PADDING;
  let y = top;

  for (const chip of chips) {
    context.font = chip.font;
    const dotWidth = chip.dot ? 24 : 0;
    const width = context.measureText(chip.text).width + 48 + dotWidth;

    if (x + width > WIDTH - PADDING) {
      x = PADDING;
      y += height + 12;
    }

    context.fillStyle = chip.back;
    roundedRect(context, x, y, width, height, 16);
    context.fill();

    if (chip.dot) {
      context.fillStyle = chip.dot;
      context.beginPath();
      context.arc(x + 24, y + height / 2, 6, 0, Math.PI * 2);
      context.fill();
    }

    context.fillStyle = chip.ink;
    context.font = chip.font;
    context.fillText(chip.text, x + 24 + dotWidth, y + 38);
    x += width + 12;
  }

  return y + height;
}

function drawDescription(
  context: CanvasRenderingContext2D,
  description: string | null,
  top: number,
): number {
  if (!description) return top;

  context.fillStyle = COLORS.body;
  context.font = `400 29px ${SANS}`;

  // Три строки: карточка должна цеплять, а не пересказывать профиль целиком.
  const lines = wrapText(context, description.replace(/\s+/g, ' '), CONTENT).slice(0, 3);
  lines.forEach((line, index) => {
    context.fillText(line, PADDING, top + 29 + index * 44);
  });

  return top + lines.length * 44;
}

/**
 * Услуги с ценой и сроком — то, ради чего визитку и открывают: заказчик решает
 * по этой паре, а не по описанию.
 */
function drawServices(
  context: CanvasRenderingContext2D,
  services: ProfileCardService[],
  top: number,
): number {
  const shown = services.slice(0, 3);
  if (shown.length === 0) return top;

  const gap = 16;
  const width = (CONTENT - gap * (shown.length - 1)) / shown.length;
  const height = 132;

  shown.forEach((service, index) => {
    const x = PADDING + index * (width + gap);

    context.fillStyle = COLORS.surface;
    roundedRect(context, x, top, width, height, 22);
    context.fill();
    context.strokeStyle = COLORS.line;
    context.lineWidth = 1;
    roundedRect(context, x, top, width, height, 22);
    context.stroke();

    context.fillStyle = COLORS.ink;
    context.font = `700 24px ${SANS}`;
    fitText(context, service.title, x + 24, top + 44, width - 48);

    context.font = `700 32px ${MONO}`;
    fitText(context, service.price ?? 'договорная', x + 24, top + 86, width - 48);

    if (service.duration) {
      context.fillStyle = COLORS.muted;
      context.font = `400 22px ${SANS}`;
      fitText(context, service.duration, x + 24, top + 116, width - 48);
    }
  });

  return top + height;
}

/** Работы занимают всё, что осталось между услугами и тегами. */
function drawWorks(
  context: CanvasRenderingContext2D,
  works: (HTMLImageElement | null)[],
  top: number,
  bottom: number,
): void {
  const height = bottom - top;
  if (height < 120) return;

  const gap = 16;
  const columns = 3;
  const width = (CONTENT - gap * (columns - 1)) / columns;

  for (let index = 0; index < columns; index += 1) {
    const x = PADDING + index * (width + gap);
    const work = works[index];

    context.save();
    roundedRect(context, x, top, width, height, 22);
    context.clip();

    if (work) {
      drawCover(context, work, x, top, width, height);
    } else {
      // Пустая ячейка вместо картинки: сетка не должна разъезжаться.
      context.fillStyle = COLORS.surface;
      context.fillRect(x, top, width, height);
    }
    context.restore();
  }
}

/** Возвращает свою же верхнюю границу: выше неё рисуются работы. */
function drawTags(context: CanvasRenderingContext2D, tags: string[], top: number): number {
  if (tags.length === 0) return top + 54;

  const height = 54;
  context.font = `700 26px ${SANS}`;
  let x = PADDING;

  for (const tag of tags.slice(0, 4)) {
    const label = `#${tag}`;
    const width = context.measureText(label).width + 44;
    if (x + width > WIDTH - PADDING) break;

    context.fillStyle = COLORS.accentSoft;
    roundedRect(context, x, top, width, height, 14);
    context.fill();

    context.fillStyle = COLORS.accentInk;
    context.fillText(label, x + 22, top + 36);
    x += width + 12;
  }

  return top;
}

/** Подвал: чей это инструмент и куда идти. Возвращает свою верхнюю границу. */
function drawFooter(context: CanvasRenderingContext2D, data: ProfileCardData): number {
  const top = HEIGHT - PADDING - 60;

  context.strokeStyle = COLORS.line;
  context.lineWidth = 1;
  context.beginPath();
  context.moveTo(PADDING, top - 28);
  context.lineTo(WIDTH - PADDING, top - 28);
  context.stroke();

  drawMark(context, PADDING, top + 6);

  context.fillStyle = COLORS.ink;
  context.font = `800 26px ${SANS}`;
  context.fillText('ArtFlow', PADDING + 62, top + 38);
  const brandWidth = context.measureText('ArtFlow').width;

  context.fillStyle = COLORS.muted;
  context.font = `400 26px ${SANS}`;
  context.fillText('· менеджер арт-заказов', PADDING + 62 + brandWidth + 12, top + 38);

  const link = data.publicUrl.replace(/^https?:\/\//, '');
  context.font = `700 26px ${MONO}`;
  const width = context.measureText(link).width + 52;

  context.fillStyle = COLORS.accentSoft;
  roundedRect(context, WIDTH - PADDING - width, top, width, 60, 16);
  context.fill();

  context.fillStyle = COLORS.accentInk;
  context.fillText(link, WIDTH - PADDING - width + 26, top + 39);

  return top - 28;
}

/** Знак приложения: круг с двумя каплями, как в шапке Mini App. */
function drawMark(context: CanvasRenderingContext2D, x: number, y: number): void {
  const size = 48;

  context.save();
  roundedRect(context, x, y, size, size, 15);
  context.clip();

  context.fillStyle = COLORS.accent;
  context.fillRect(x, y, size, size);

  context.fillStyle = '#FFC212';
  context.beginPath();
  context.arc(x + 6, y + size - 6, 16, 0, Math.PI * 2);
  context.fill();

  context.fillStyle = '#F9B0C3';
  context.beginPath();
  context.arc(x + size - 14, y + 16, 10, 0, Math.PI * 2);
  context.fill();
  context.restore();
}

/** Картинка вписывается «по обрезке»: пропорции важнее того, чтобы влезло всё. */
function drawCover(
  context: CanvasRenderingContext2D,
  image: HTMLImageElement,
  x: number,
  y: number,
  width: number,
  height: number,
): void {
  const scale = Math.max(width / image.width, height / image.height);
  const drawWidth = image.width * scale;
  const drawHeight = image.height * scale;

  context.drawImage(
    image,
    x + (width - drawWidth) / 2,
    y + (height - drawHeight) / 2,
    drawWidth,
    drawHeight,
  );
}

/** Картинки лежат на том же домене, поэтому canvas не «пачкается». */
function loadImage(url: string | null): Promise<HTMLImageElement | null> {
  if (!url) return Promise.resolve(null);

  return new Promise((resolve) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    // Без картинки карточка всё равно рисуется — с пустой ячейкой.
    image.onerror = () => resolve(null);
    image.src = url;
  });
}

function wrapText(context: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const lines: string[] = [];
  let line = '';

  for (const word of text.split(' ')) {
    const candidate = line ? `${line} ${word}` : word;
    if (context.measureText(candidate).width > maxWidth && line) {
      lines.push(line);
      line = word;
    } else {
      line = candidate;
    }
  }

  if (line) lines.push(line);
  return lines;
}

/** Длинное имя не должно уезжать за край: обрезаем многоточием. */
function fitText(
  context: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
): void {
  let value = text;

  while (value.length > 1 && context.measureText(value).width > maxWidth) {
    value = `${value.slice(0, -2)}…`;
  }

  context.fillText(value, x, y);
}

function roundedRect(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
): void {
  context.beginPath();
  context.roundRect(x, y, width, height, radius);
}

function humanDate(day: string): string {
  return new Date(`${day}T00:00:00.000Z`).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  });
}

function toBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Пустая картинка'))),
      'image/png',
    );
  });
}
